export type EventSourceClientHandlers = {
    onEvent: (type: string, data: unknown) => void;
    onError?: (error: Event) => void;
};

export type EventSourceClientOptions = {
    url: string;
    handlers: EventSourceClientHandlers;
    withCredentials?: boolean;
    reconnectDelayMs?: number;
    eventTypes?: string[];
};

export type EventSourceClientSubscription = {
    close: () => void;
};

const DEFAULT_EVENT_TYPES = [
    'session.status',
    'message.delta',
    'message.created',
    'context.usage',
    'heartbeat',
    'tool.result',
    'tool.elicitation',
    'analysis.verdict',
    'analysis.error',
    'error'
];

export class EventSourceClient {
    public open(options: EventSourceClientOptions): EventSourceClientSubscription {
        let closed = false;
        let source: EventSource | null = null;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        const delay = options.reconnectDelayMs ?? 2000;
        const eventTypes = options.eventTypes ?? DEFAULT_EVENT_TYPES;

        const emit = (type: string, raw: string): void => {
            let data: unknown = raw;
            try {
                data = JSON.parse(raw);
            } catch {
                // keep raw string
            }
            options.handlers.onEvent(type, data);
        };

        const connect = (): void => {
            if (closed) {
                return;
            }
            source = new EventSource(options.url, { withCredentials: options.withCredentials ?? true });
            source.onmessage = (event) => {
                emit(event.type || 'message', event.data);
            };
            for (const type of eventTypes) {
                source.addEventListener(type, ((event: MessageEvent) => {
                    emit(type, event.data);
                }) as EventListener);
            }
            source.onerror = (error) => {
                options.handlers.onError?.(error);
                source?.close();
                source = null;
                if (!closed) {
                    reconnectTimer = setTimeout(connect, delay);
                }
            };
        };

        connect();

        return {
            close: () => {
                closed = true;
                if (reconnectTimer) {
                    clearTimeout(reconnectTimer);
                }
                source?.close();
                source = null;
            }
        };
    }
}
